import { Link, useSearchParams } from 'react-router-dom';
import { get } from '../api';
import { DeactivatedName } from '../components/DeactivatedName';
import { RolePill } from '../components/RolePill';
import { SectionTabs } from '../components/SectionTabs';
import { useLoad } from '../hooks';

interface Row {
  id: number;
  display_name: string;
  email: string;
  phone: string;
  mobile: string;
  roles: string[];
  state?: string;
}
const PAGE = 50;
const TABS: [string, string, string][] = [
  ['client', 'Clients & insurers', 'client'],
  ['referrer', 'OT / rehab', 'referrer'],
  ['contractor', 'Contractors', 'contractor'],
  ['customer', 'Customers', 'customer'],
  ['', 'All', ''],
];

export function PeopleList() {
  const [params, setParams] = useSearchParams();
  const role = params.get('role') ?? '';
  const q = params.get('q') ?? '';
  // Only active people here; deactivated ones are restored or deleted from Settings → Deactivated.
  const state = 'active';
  const offset = Number(params.get('offset') ?? 0) || 0;

  const list = useLoad(() => {
    const qs = new URLSearchParams({ state, limit: String(PAGE), offset: String(offset) });
    if (role) qs.set('role', role);
    if (q) qs.set('q', q);
    return get<{ items: Row[] }>(`/api/contacts?${qs}`);
  }, [role, q, state, offset]);

  /** Change filters; any filter change goes back to the first page. */
  const change = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries({ offset: '', ...patch })) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next);
  };
  const search = (value: string) => value.trim() !== q && change({ q: value.trim() });
  const counts = useLoad(() => get<Record<string, number>>('/api/contacts/counts'), []);
  const items = Array.isArray(list.data?.items) ? list.data!.items : [];

  return (
    <>
      <div className="row">
        <h1>People</h1>
        <Link className="button" to="/people/new">
          Add person
        </Link>
      </div>
      <SectionTabs
        label="People by role"
        current={role}
        tabs={TABS.map(([key, label, count]) => ({
          key,
          label:
            count && counts.data && typeof counts.data[count] === 'number'
              ? `${label} (${counts.data[count]})`
              : label,
        }))}
        to={(k) => (k ? `/people?role=${k}` : '/people')}
      />
      <div className="filters" role="search">
        <label>
          Search
          <input
            type="search"
            defaultValue={q}
            onKeyDown={(e) => e.key === 'Enter' && search(e.currentTarget.value)}
            onBlur={(e) => search(e.currentTarget.value)}
          />
        </label>
        <Link className="muted filters-aside" to="/settings?tab=deactivated">
          Deactivated people
        </Link>
      </div>

      {list.error && (
        <p role="alert" className="error">
          {list.error}
        </p>
      )}
      {list.loading && <p>Loading…</p>}
      {!list.loading && !list.error && items.length === 0 && (
        <p className="empty">
          {q || role ? 'No one matches those filters.' : 'No people yet. Add the first one.'}
        </p>
      )}
      {items.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Roles</th>
              <th>Email</th>
              <th>Phone</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.state === 'inactive' ? (
                    <DeactivatedName name={p.display_name || '(no name)'} />
                  ) : (
                    <Link to={`/people/${p.id}`}>{p.display_name || '(no name)'}</Link>
                  )}
                </td>
                <td>
                  <span className="role-pills">
                    {p.roles.map((r) => (
                      <RolePill key={r} role={r} />
                    ))}
                  </span>
                </td>
                <td>{p.email}</td>
                <td>{p.mobile || p.phone}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* Only when there is another page to go to. */}
      {(offset > 0 || items.length >= PAGE) && (
        <div className="pager">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => change({ offset: String(Math.max(0, offset - PAGE) || ''), q })}
          >
            Previous
          </button>
          <button
            type="button"
            disabled={items.length < PAGE}
            onClick={() => change({ offset: String(offset + PAGE), q })}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}
